import { isAxiosError } from "axios";

/**
 * An access refusal is not an outage: the page presents it as a state, with no
 * retry offered. The distinction lives here, next to the codes themselves, so
 * that a new code must be filed under one of the two families.
 */
const PURCHASE_POINTS_ACCESS_CODES = [
  "cashback_disabled",
  "organization_feature_disabled",
  "consumer_not_onboarded",
  // Read from the session before any request, so the page never calls at all.
  // Also served by the backend, for a toggle made in another tab.
  "employee_opted_out",
  // Decided by the platform, not by him: an account no longer active, or an
  // employee who has left the company.
  "account_locked",
  "employee_departed",
] as const;

const PURCHASE_POINTS_TECHNICAL_CODES = [
  "provider_unavailable",
  "provider_error",
  "offer_not_found",
  "brand_not_found",
  // A conversion that raced and lost: the points are intact and clicking again
  // works. An outage, not a refusal — the employee did nothing wrong.
  "conversion_interrupted",
  // Our configuration or our data, which no retry resolves. Filed as technical
  // all the same: the employee is not the one being refused.
  "conversion_impossible",
] as const;

/**
 * Neither a refusal of access nor an outage: the employee may convert, nothing
 * is broken, there is simply nothing left to convert. Rendering it as a failure
 * would offer a retry button for a screen that is merely out of date.
 */
const PURCHASE_POINTS_BUSINESS_CODES = [
  "purchase_points_no_convertible_points",
] as const;

const PURCHASE_POINTS_ERROR_CODES = [
  ...PURCHASE_POINTS_ACCESS_CODES,
  ...PURCHASE_POINTS_BUSINESS_CODES,
  ...PURCHASE_POINTS_TECHNICAL_CODES,
] as const;

const WHOLE_SECONDS = /^\d+$/;

export type PurchasePointsErrorCode = typeof PURCHASE_POINTS_ERROR_CODES[number] | "unknown";

export type PurchasePointsAccessCode = typeof PURCHASE_POINTS_ACCESS_CODES[number];

export type PurchasePointsBusinessCode = typeof PURCHASE_POINTS_BUSINESS_CODES[number];

export type PurchasePointsTechnicalCode =
  Exclude<PurchasePointsErrorCode, PurchasePointsAccessCode | PurchasePointsBusinessCode>;

export const isAccessCode = (code: PurchasePointsErrorCode): code is PurchasePointsAccessCode =>
  PURCHASE_POINTS_ACCESS_CODES.some((accessCode) => accessCode === code);

export const isBusinessCode = (code: PurchasePointsErrorCode): code is PurchasePointsBusinessCode =>
  PURCHASE_POINTS_BUSINESS_CODES.some((businessCode) => businessCode === code);

export type PurchasePointsError = {
  code: PurchasePointsErrorCode
  detail: string | null
  retryAfterSeconds: number | null
};

const UNKNOWN_ERROR: PurchasePointsError = { code: "unknown", detail: null, retryAfterSeconds: null };

const isHandledCode = (value: unknown): value is PurchasePointsErrorCode =>
  typeof value === "string" && PURCHASE_POINTS_ERROR_CODES.some((code) => code === value);

/**
 * The backend only emits `Retry-After` when the provider supplied one, and the
 * RFC also allows an HTTP date. That second format is ignored rather than risk
 * an absurd delay coming out of a loose reading.
 */
const readRetryAfterSeconds = (rawRetryAfter: unknown): number | null =>
  (typeof rawRetryAfter === "string" && WHOLE_SECONDS.test(rawRetryAfter)
    ? Number(rawRetryAfter)
    : null);

/**
 * The backend only guarantees a non-empty `detail`: an empty string would come
 * from an upstream bug, and showing it would produce a blank message worse than
 * the fallback.
 */
const readDetail = (data: unknown): string | null => {
  const detail = (data as { errors?: { detail?: unknown }[] } | undefined)?.errors?.[0]?.detail;
  return typeof detail === "string" && detail.length > 0 ? detail : null;
};

export const toPurchasePointsError = (error: unknown): PurchasePointsError => {
  if (!isAxiosError(error) || !error.response) return UNKNOWN_ERROR;

  const businessCode = (error.response.data as { code?: unknown } | undefined)?.code;

  return {
    code: isHandledCode(businessCode) ? businessCode : "unknown",
    detail: readDetail(error.response.data),
    retryAfterSeconds: readRetryAfterSeconds(error.response.headers["retry-after"]),
  };
};
